import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Badge, Card, DataState, PageHeader, Stat } from '@/components/Common'
import { useFetch } from '@/lib/hooks'
import type { QueryLogSummary, QueryLogRow } from '@/lib/api'

/**
 * What every modality has actually asked for, and what it got.
 *
 * This is the answer to the only support call this system generates. The
 * column that answers it is `matched_rule_ids`: empty means no rule covered
 * the caller, which is the commonest cause of an empty worklist and is
 * invisible from every other page.
 *
 * *** identifier_summary MAY CONTAIN PHI. *** It holds whatever the modality
 * matched on, and a query by patient name puts a patient name in it. That is
 * what makes the log useful, and it is why the daemon purges it on a retention
 * setting.
 */
export function QueryLog() {
  const [hours, setHours] = useState(24)
  const [callingAe, setCallingAe] = useState('')
  const [filter, setFilter] = useState<'all' | 'unmatched' | 'empty' | 'failed'>('all')

  const query = new URLSearchParams({ hours: String(hours), limit: '300' })
  if (callingAe) query.set('calling_ae', callingAe)
  if (filter === 'unmatched') query.set('unmatched_only', 'true')
  if (filter === 'empty') query.set('empty_only', 'true')
  if (filter === 'failed') query.set('failed_only', 'true')

  const rows = useFetch<{ items: QueryLogRow[]; more: boolean; limit: number }>(
    `/api/v1/query-log?${query.toString()}`,
    30000,
  )
  const summary = useFetch<QueryLogSummary>(`/api/v1/query-log/summary?hours=${hours}`, 30000)

  return (
    <>
      <PageHeader
        title="Query log"
        subtitle="One row per C-FIND: who asked, which rules matched, and what came back."
      />

      <Card title={`Last ${hours} hours`}>
        <DataState loading={summary.loading} error={summary.error} empty={false} />
        {summary.data && (
          <>
            <div className="stat-row">
              <Stat label="Queries" value={summary.data.queries.toLocaleString()} />
              <Stat
                label="No rule matched"
                value={summary.data.no_rule.toLocaleString()}
                tone={summary.data.no_rule > 0 ? 'error' : 'ok'}
              />
              <Stat
                label="Empty results"
                value={summary.data.empty.toLocaleString()}
                tone={summary.data.empty > 0 ? 'warn' : 'ok'}
              />
              <Stat
                label="Failed"
                value={summary.data.failed.toLocaleString()}
                tone={summary.data.failed > 0 ? 'error' : 'ok'}
              />
              <Stat
                label="Truncated"
                value={summary.data.truncated.toLocaleString()}
                tone={summary.data.truncated > 0 ? 'warn' : 'idle'}
                hint="hit the match cap"
              />
              <Stat
                label="Typical"
                value={summary.data.avg_ms != null ? `${summary.data.avg_ms} ms` : '—'}
                hint={`slowest ${summary.data.max_ms} ms`}
              />
            </div>

            {summary.data.by_caller.length > 0 && (
              <>
                <div className="subhead">By caller — anything with a problem first</div>
                <table className="ae-table">
                  <thead>
                    <tr>
                      <th>Calling AE</th>
                      <th>From</th>
                      <th>Queries</th>
                      <th>No rule</th>
                      <th>Empty</th>
                      <th>Last seen</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {summary.data.by_caller.map((caller, i) => (
                      <tr key={i} className={caller.no_rule > 0 ? 'row-error' : undefined}>
                        <td className="mono">{caller.calling_ae}</td>
                        <td className="mono">{caller.source_ip}</td>
                        <td className="num">{caller.queries}</td>
                        <td className="num">
                          {caller.no_rule > 0 ? (
                            <span className="warn-text">{caller.no_rule}</span>
                          ) : (
                            0
                          )}
                        </td>
                        <td className="num">{caller.empty}</td>
                        <td className="hint">{caller.last_seen}</td>
                        <td className="actions">
                          <button
                            className="btn btn-small"
                            onClick={() => setCallingAe(caller.calling_ae)}
                          >
                            Filter
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {summary.data.no_rule > 0 && (
              <div className="notice notice-warn">
                A caller matching no rule is authorised and simply has no rule
                covering it, so it receives an empty worklist and a Success
                status — indistinguishable at the modality from having no work.
                Use the <Link to="/simulator">simulator</Link> on one of the
                callers above to see exactly which clause every rule failed on.
              </div>
            )}
          </>
        )}
      </Card>

      <Card title="Queries">
        <div className="filter-grid">
          <label className="field">
            <span className="field-label">Window</span>
            <select value={hours} onChange={(e) => setHours(Number(e.target.value))}>
              <option value={1}>last hour</option>
              <option value={24}>last 24 hours</option>
              <option value={168}>last week</option>
              <option value={720}>last 30 days</option>
            </select>
          </label>
          <label className="field">
            <span className="field-label">Show</span>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as typeof filter)}
            >
              <option value="all">everything</option>
              <option value="unmatched">nothing served (no association or rule)</option>
              <option value="empty">empty results</option>
              <option value="failed">failures</option>
            </select>
          </label>
          <label className="field">
            <span className="field-label">Calling AE</span>
            <input value={callingAe} onChange={(e) => setCallingAe(e.target.value)} />
          </label>
          <div className="filter-actions">
            <button className="btn btn-small" onClick={() => { setCallingAe(''); setFilter('all') }}>
              Clear
            </button>
          </div>
        </div>

        <DataState
          loading={rows.loading}
          error={rows.error}
          empty={(rows.data?.items ?? []).length === 0}
          emptyMessage="Nothing in this window. If the daemon is running and modalities are polling, check that the query log is enabled."
        />

        {(rows.data?.items ?? []).length > 0 && (
          <div className="table-wrap">
            <table className="ae-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Caller</th>
                  <th>Port</th>
                  <th>Served via</th>
                  <th>Scoped to</th>
                  <th>Matches</th>
                  <th>Status</th>
                  <th>Asked for</th>
                </tr>
              </thead>
              <tbody>
                {(rows.data?.items ?? []).map((row) => (
                  <tr
                    key={row.id}
                    className={
                      row.matched_rule_ids === ''
                        ? 'row-error'
                        : row.dimse_status !== '0000' && row.dimse_status !== 'FE00'
                          ? 'row-warn'
                          : undefined
                    }
                  >
                    <td className="hint mono">{row.occurred_at.slice(11, 19)}</td>
                    <td>
                      <span className="mono">{row.calling_ae}</span>
                      <div className="hint mono">{row.source_ip}</div>
                    </td>
                    <td className="num">{row.listener_port}</td>
                    <td className="mono">
                      {row.selected_by ? (
                        <>
                          {row.selected_by}
                          {row.matched_rule_ids && (
                            <div className="hint">rule {row.matched_rule_ids}</div>
                          )}
                        </>
                      ) : (
                        <span className="warn-text">nothing matched</span>
                      )}
                    </td>
                    <td className="hint">
                      {row.facility_scope || <span className="hint">not scoped</span>}
                      {row.department_scope && <div>{row.department_scope}</div>}
                    </td>
                    <td className="num">
                      {row.matches}
                      {row.truncated && <div className="warn-text">capped</div>}
                    </td>
                    <td>
                      <Badge state={statusLabel(row.dimse_status)} />
                      <div className="hint">{row.elapsed_ms} ms</div>
                    </td>
                    <td className="truncate hint">{row.identifier_summary}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {rows.data?.more && (
          <div className="notice notice-warn">
            More than {rows.data.limit} rows match. Narrow the window or the
            filters.
          </div>
        )}

        <p className="table-footnote">
          "Asked for" is the modality's own matching keys and{' '}
          <strong>can contain patient data</strong>. The daemon purges this table
          on its retention setting; it is the operational record, not the
          clinical one.
        </p>
      </Card>
    </>
  )
}

function statusLabel(status: string): string {
  switch (status) {
    case '0000':
      return 'SUCCESS'
    case 'A700':
      return 'TRUNCATED'
    case 'FE00':
      return 'CANCELLED'
    default:
      return `FAILED ${status}`
  }
}
